import { NAVIGATION_DATA } from '../constants';
import type { PrizeDistributionData } from '../types';
import type { Game, GameFlash, GameHoldem, GameMax, GameOmaha, GameTypeName } from '../types/Game';
import { GameType } from '../types/Game';

export const mapGameData = ({
  games = NAVIGATION_DATA,
  isHoldemEnabled = true,
  isFlashEnabled = false,
  isMaxEnabled = false,
  isOmahaEnabled = false
}) => {
  const mappedData = [];

  if (isHoldemEnabled) {
    mappedData.push(games.Holdem);
  }
  if (isFlashEnabled) {
    mappedData.push(games.HoldemFlash);
  }
  if (isMaxEnabled) {
    mappedData.push(games.HoldemMax);
  }
  if (isOmahaEnabled) {
    mappedData.push(games.Omaha);
  }

  return mappedData;
};

export const isHoldem = (tile: Game): tile is GameHoldem =>
  tile.game === GameType.Holdem && !tile.isMax;

export const isHoldemFlash = (tile: Game): tile is GameFlash => tile.isFlash;

export const isHoldemMax = (tile: Game): tile is GameMax => tile.isMax;

export const isOmaha = (tile: Game): tile is GameOmaha => tile.game === GameType.Omaha;

export const filterTilesForPage = (games: Game[], checkGameType: (game: Game) => boolean): Game[] =>
  games.filter(checkGameType);

export const mapTilesToDisplay = (games: Game[], selectedPage: keyof typeof GameType) => {
  let tiles: Game[] = [];

  if (GameType.Holdem === selectedPage) {
    tiles = filterTilesForPage(games, isHoldem);
  } else if (GameType.HoldemFlash === selectedPage) {
    tiles = filterTilesForPage(games, isHoldemFlash);
  } else if (GameType.HoldemMax === selectedPage) {
    tiles = filterTilesForPage(games, isHoldemMax);
  } else if (GameType.Omaha === selectedPage) {
    tiles = filterTilesForPage(games, isOmaha);
  }

  return tiles;
};

export const findSmallestBuyInId = (data: Game[]): number => {
  let minBuyIn = Number.POSITIVE_INFINITY;
  let id = -1;

  for (const game of data) {
    const buyInNumber = parseFloat(game.buyIn.replace(/[^0-9.]/g, ''));
    if (buyInNumber < minBuyIn) {
      minBuyIn = buyInNumber;
      id = game.scriptId;
    }
  }

  return id;
};

export const isGameOfType = (game: Game, gameType: GameTypeName) => {
  if (gameType === GameType.Holdem || gameType === GameType.Omaha) {
    return game.game === gameType && !game.isMax;
  }
  if (gameType === GameType.HoldemFlash) {
    return game.isFlash;
  }
  if (gameType === GameType.HoldemMax) {
    return game.isMax;
  }
  return true;
};

export const getGameParams = (prizeData: PrizeDistributionData, gameType: GameTypeName) => {
  const timeBank =
    gameType === GameType.HoldemFlash ? prizeData.flashModTimeBank : prizeData.timeBank;
  const initialChips =
    gameType === GameType.HoldemFlash
      ? prizeData.vars?.[0].drawLevels?.[0].initialChips
      : prizeData.modLevels?.[0].drawLevels?.[0].initialChips;
  const drawLevels =
    gameType === GameType.HoldemFlash
      ? prizeData.vars?.[0].drawLevels
      : prizeData.modLevels?.[0].drawLevels;

  const { modLevels, currency, scalePM, buyin, rake } = prizeData;

  return {
    timeBank,
    initialChips,
    drawLevels,
    modLevels,
    currency,
    scalePM,
    buyin,
    rake
  };
};

export const getGameType = ({
  gameType,
  flash,
  max
}: {
  gameType: number;
  flash: boolean;
  max: boolean;
}) => {
  if (gameType === 4) return GameType.Omaha;
  if (max === true) return GameType.HoldemMax;
  if (flash === true) return GameType.HoldemFlash;
  return GameType.Holdem;
};

export const splitPrize = (prize?: string): [string, string | undefined] => {
  if (!prize) return ['', undefined];
  const [firstLine, ...rest] = prize.split(' ');
  const secondLine = rest.join(' ') || undefined;
  return [firstLine, secondLine];
};

export const isDeleteOperation = (game: object) =>
  !Object.values({ ...game, scriptId: null }).filter((val) => !!val).length;
